import { InvoiceGroup } from '@rideiq/organizers/features/InvoiceGroups/types';

import { RiderLocation } from '../RiderLocations/Location/Location.types';
import { RiderLocationFormData } from '../RiderLocations/RiderLocationForm/RiderLocationForm.types';

export type SectionKey = 'basic-info' | 'contacts' | 'locations' | 'rider-services';

export const SECTION_KEYS: SectionKey[] = ['basic-info', 'contacts', 'locations', 'rider-services'];

export const SECTION_LABELS: Record<SectionKey, string> = {
  'basic-info': 'Basic Information',
  contacts: 'Contacts',
  locations: 'Locations',
  'rider-services': 'Rider Services',
};

export const SECTION_SUBTITLES: Record<SectionKey, string> = {
  'basic-info': 'Name, date of birth, and other identifying information',
  contacts: 'Caregiver and additional contacts',
  locations: 'Addresses associated with this rider',
  'rider-services': 'Safety settings, services, and rider preferences',
};

export type SupportSetting = {
  supportType: string;
  details: string;
  selected: boolean;
};

export interface LocationDraft {
  draftId: string;
  location: RiderLocation;
  formData: RiderLocationFormData;
  isNew: boolean;
  isDirty?: boolean;
  // Local flag only - NOT sent to API. Field names changed on last edit, for FullStory's location_edited event.
  changedFields?: string[];
}

export type PhotoAttribute = {
  attachment?: File | FileList | string | null;
  id?: string;
  _destroy?: string;
  url?: string;
};

export type SharedBillingRiderValues = {
  residenceAccountId: Nullable<number>;
  residenceAccountContactName: string;
  residenceAccountContactEmail: string;
  residenceAccountContactId?: Nullable<number>;
};

export type ContactRole = 'caregiver' | 'emergency_contact';

export type ContactFormValue = {
  id?: number;
  firstName: string;
  lastName: string;
  role: ContactRole;
  relationship: string;
  phoneNumber: string;
  email: string;
};

export const emptyContact = (): ContactFormValue => ({
  firstName: '',
  lastName: '',
  role: 'caregiver',
  relationship: '',
  phoneNumber: '',
  email: '',
});

export type RiderFormValues = {
  // Rider Services
  codeWord: string;
  frontSeatAuthorized: boolean;
  boosterSeat: boolean;
  dateOfBirth: Nullable<Date> | null;
  services: string[];
  // Local flag only - NOT sent to API. Raw incompatible-services messages from the last
  // failed create/update response, used to highlight conflicting OptionCards.
  serviceConflictMessages: string[];
  supportSettings: SupportSetting[];
  riderNotes: string;
  // Locations — snapshot of associated location ids at load time (for diffing removals at submit)
  locationDrafts: LocationDraft[];
  originalLocationIds: number[];
  // Basic Info — Identity & contact
  firstName: string;
  lastName: string;
  gender: string;
  email: string | null;
  mobilePhoneNumber: string;
  photosAttributes: PhotoAttribute[];
  sisImportUsed: boolean;
  // Basic Info — Account & billing
  category: string | null;
  externalId: string;
  invoiceGroupId: Nullable<number>;
  invoiceGroup?: InvoiceGroup | null;
  // Basic Info — Toggle cards
  isAide: boolean;
  isSharedBillingRider: boolean;
  sharedBillingRider: SharedBillingRiderValues;
  // Contacts — snapshot of associated contact ids at load time (for diffing removals at submit)
  contacts: ContactFormValue[];
  originalContactIds: number[];
};
